import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, Mail, FileText, Download } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Get session_id from URL query params (Stripe redirect) or from state (old flow)
  const sessionIdFromUrl = searchParams.get('session_id');
  const { subscriptionId: subscriptionIdFromState, showDocuments } = location.state || {};
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  
  useEffect(() => {
    // Handle Stripe redirect with session_id
    if (sessionIdFromUrl) {
      const processStripeSuccess = async () => {
        try {
          // Call backend to process the successful payment
          const response = await axios.get(`${API}/subscription/success`, {
            params: { session_id: sessionIdFromUrl }
          });
          
          setSubscriptionData(response.data);
          setLoading(false);
        } catch (err) {
          console.error('Error processing payment success:', err);
          setError(true);
          setLoading(false);
        }
      };
      
      processStripeSuccess();
    } 
    // Handle old flow with subscriptionId from state
    else if (subscriptionIdFromState) {
      const fetchSubscription = async () => {
        try {
          await new Promise(resolve => setTimeout(resolve, 1500));
          setLoading(false);
        } catch (err) {
          setError(true);
          setLoading(false);
        }
      };
      
      fetchSubscription();
    } 
    // No valid data, redirect home
    else {
      navigate('/');
    }
  }, [sessionIdFromUrl, subscriptionIdFromState, navigate]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#e8f5ef] via-[#f0f9f4] to-white flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-12 text-center">
            <Loader2 className="h-16 w-16 animate-spin mx-auto mb-4 text-[#52a675]" />
            <h2 className="text-xl font-bold mb-2">Traitement en cours...</h2>
            <p className="text-gray-600">Veuillez patienter</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e8f5ef] via-[#f0f9f4] to-white">
      <section className="pt-20 py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto">
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-[#52a675] rounded-full mb-6">
                <CheckCircle className="h-12 w-12 text-white" />
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Paiement confirmé !
              </h1>
              
              <p className="text-xl text-gray-600 mb-8">
                Votre abonnement CarMaintenance est maintenant actif
              </p>
            </div>

            <Card className="mb-8">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4 mb-6">
                  <Mail className="h-6 w-6 text-[#52a675] mt-1" />
                  <div className="text-left">
                    <h3 className="font-bold text-lg mb-2">Documents envoyés par email</h3>
                    <p className="text-gray-600">
                      Vous allez recevoir dans quelques instants un email contenant :
                    </p>
                    <ul className="list-disc list-inside mt-2 space-y-1 text-gray-600">
                      <li>Votre facture</li>
                      <li>Votre contrat d'abonnement signé électroniquement</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Important :</strong> Veuillez vérifier votre dossier spam si vous ne recevez pas
                    l'email dans les 5 prochaines minutes.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="mb-8">
              <CardContent className="p-8">
                <h3 className="font-bold text-lg mb-4">Que se passe-t-il maintenant ?</h3>
                <div className="space-y-4 text-left">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-[#52a675] text-white rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      1
                    </div>
                    <p className="text-gray-600">
                      Votre premier prélèvement automatique aura lieu aujourd'hui
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-[#52a675] text-white rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      2
                    </div>
                    <p className="text-gray-600">
                      Vous pouvez utiliser votre abonnement dès maintenant pour l'entretien de votre véhicule
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-[#52a675] text-white rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      3
                    </div>
                    <p className="text-gray-600">
                      Consultez nos garages partenaires pour prendre rendez-vous
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/nos-partenaires')}
                className="bg-[#52a675] hover:bg-[#4a9566]"
              >
                Voir nos garages partenaires
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
              >
                Retour à l'accueil
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SuccessPage;
